import { useRef, useEffect, useState, useCallback, useMemo } from 'react';

export interface FadeInConfig {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
  reverseOnExit?: boolean;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'scale' | 'fade';
  distance?: number;
}

export interface FadeInState {
  isVisible: boolean;
  isAnimating: boolean;
  hasAnimated: boolean;
  direction: 'enter' | 'exit';
}

const defaultConfig: Required<FadeInConfig> = {
  threshold: 0.1,
  rootMargin: '0px 0px -10% 0px',
  triggerOnce: true,
  reverseOnExit: false,
  delay: 0,
  direction: 'up',
  distance: 60,
};

export function useFadeInOnScroll<T extends HTMLElement>(
  config: FadeInConfig = {}
) {
  const ref = useRef<T | null>(null);
  const [state, setState] = useState<FadeInState>({
    isVisible: false,
    isAnimating: false,
    hasAnimated: false,
    direction: 'enter',
  });
  const [mounted, setMounted] = useState(false);

  const mergedConfig = useMemo(() => ({ ...defaultConfig, ...config }), [config]);

  const updateState = useCallback((newState: Partial<FadeInState>) => {
    setState(prev => ({ ...prev, ...newState }));
  }, []);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const isIntersecting = entry.isIntersecting;
        const wasVisible = state.isVisible;
        
        if (isIntersecting && !wasVisible) {
          // Element entering viewport
          updateState({
            isVisible: true,
            isAnimating: true,
            direction: 'enter',
          });

          // Add delay if configured
          setTimeout(() => {
            updateState({ isAnimating: false });
            if (mergedConfig.triggerOnce) {
              updateState({ hasAnimated: true });
            }
          }, mergedConfig.delay + 600);
        } else if (!isIntersecting && wasVisible && mergedConfig.reverseOnExit && !mergedConfig.triggerOnce) {
          // Element exiting viewport (reverse animation)
          updateState({
            isVisible: false,
            isAnimating: true,
            direction: 'exit',
          });

          setTimeout(() => {
            updateState({ isAnimating: false });
          }, 600);
        }
      });
    },
    [state.isVisible, mergedConfig, updateState]
  );

  useEffect(() => {
    setMounted(true);
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: mergedConfig.threshold,
      rootMargin: mergedConfig.rootMargin,
    });

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [handleIntersection, mergedConfig.threshold, mergedConfig.rootMargin]);

  // Generate CSS animation properties
  const getAnimationStyles = useCallback(() => {
    const { direction, distance } = mergedConfig;
    const { isVisible, isAnimating, direction: animDirection } = state;

    if (!isAnimating && !isVisible && mergedConfig.triggerOnce && state.hasAnimated) {
      return {};
    }

    const isEntering = animDirection === 'enter';

    const transforms = {
      up: `translateY(${isEntering ? 0 : distance}px)`,
      down: `translateY(${isEntering ? 0 : -distance}px)`,
      left: `translateX(${isEntering ? 0 : distance}px)`,
      right: `translateX(${isEntering ? 0 : -distance}px)`,
      scale: `scale(${isEntering ? 1 : 0.8})`,
      fade: 'translateY(0)',
    };

    return {
      opacity: isEntering ? (isVisible ? 1 : 0) : (isVisible ? 0 : 1),
      transform: transforms[direction],
      transition: `all 0.8s cubic-bezier(0.25, 0.1, 0.25, 1) ${mergedConfig.delay}ms`,
      willChange: isAnimating ? 'transform, opacity' : 'auto',
    };
  }, [state, mergedConfig]);

  return [
    ref,
    state.isVisible && mounted,
    getAnimationStyles(),
  ] as const;
}

// Hook legacy para compatibilidade
export function useFadeInOnScrollLegacy<T extends HTMLElement>() {
  const [ref, isVisible] = useFadeInOnScroll<T>();
  return [ref, isVisible] as const;
}
